import { usePwaInstall } from '@logistech/ui';
import { Link, useLocation } from '@tanstack/react-router';
import { Download, MoreHorizontal, User, type LucideIcon } from 'lucide-react';
import { useState } from 'react';

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { FileRouteTypes } from '@/routeTree.gen';

export interface NavItem {
  to: FileRouteTypes['to'];
  label: string;
  // Libellé court sous l'icône (colonne de ~75 px sur iPhone SE)
  shortLabel?: string;
  icon: LucideIcon;
}

// Ordre de priorité des 4 icônes : un rôle sans l'une de ces entrées voit la suivante à sa place
const PRIMARY_ORDER: FileRouteTypes['to'][] = ['/dashboard', '/map', '/missions', '/alerts', '/fleet'];

const itemClass = 'flex h-full w-full flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors';

/**
 * Navigation du bas sous md (la sidebar prend le relais au-dessus). items : entrées de la sidebar déjà filtrées
 * par permission ; les 4 premières de PRIMARY_ORDER ont leur icône, les autres vont dans le Sheet « Autres ».
 * Candidat refactor @logistech/ui-components (TD-028) : même composant dans admin-platform, pwa-manager et pwa-driver.
 */
export const BottomNav = ({ items }: { items: NavItem[] }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { canPrompt, promptInstall } = usePwaInstall();

  const primary = PRIMARY_ORDER
    .map((to) => items.find((item) => item.to === to))
    .filter((item): item is NavItem => !!item)
    .slice(0, 4);
  const others = items.filter((item) => !primary.includes(item));
  const isActive = (to: string) => location.pathname.startsWith(to);
  const othersActive = others.some((item) => isActive(item.to)) || isActive('/profile');

  return (
    <nav
      aria-label="Navigation mobile"
      className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/90 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
    >
      <ul className="grid h-16 grid-cols-5">
        {primary.map(({ to, label, shortLabel, icon: Icon }) => (
          <li key={to}>
            <Link
              to={to}
              className={cn(itemClass, isActive(to) ? 'text-primary' : 'text-muted-foreground')}
              aria-current={isActive(to) ? 'page' : undefined}
            >
              <Icon className="h-5 w-5" aria-hidden />
              {shortLabel ?? label}
            </Link>
          </li>
        ))}
        <li className="col-start-5">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger className={cn(itemClass, othersActive ? 'text-primary' : 'text-muted-foreground')}>
              <MoreHorizontal className="h-5 w-5" aria-hidden />
              Autres
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="max-h-[80vh] overflow-y-auto rounded-t-xl pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
              aria-describedby={undefined}
            >
              <SheetHeader>
                <SheetTitle>Autres</SheetTitle>
              </SheetHeader>
              {/* 2 colonnes : jusqu'à 13 entrées tiennent sans défilement sur un iPhone SE */}
              <ul className="mt-4 grid grid-cols-2 gap-1">
                {[...others, { to: '/profile' as const, label: 'Mon profil', icon: User }].map(({ to, label, icon: Icon }) => (
                  <li key={to}>
                    <Link
                      to={to}
                      onClick={() => setOpen(false)}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium hover:bg-muted',
                        isActive(to) && 'bg-muted text-primary',
                      )}
                    >
                      <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
                      {label}
                    </Link>
                  </li>
                ))}
                {canPrompt && (
                  <li>
                    <button
                      onClick={() => { setOpen(false); void promptInstall(); }}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-3 text-left text-sm font-medium hover:bg-muted"
                    >
                      <Download className="h-5 w-5 text-muted-foreground" aria-hidden />
                      Installer l&apos;application
                    </button>
                  </li>
                )}
              </ul>
            </SheetContent>
          </Sheet>
        </li>
      </ul>
    </nav>
  );
};
